import React, { useEffect, useState } from 'react';
import {
  EditBase,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useUpdate,
  required,
  useDataProvider,
  SelectInput,
  email,
  useEditContext,
} from "react-admin";
import Box from "@mui/material/Box";
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';

const UserEditForm: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const { id } = useParams();
  const { record } = useEditContext();
  const formContext = useFormContext();
  const setValue = formContext?.setValue;

  const [roles, setRoles] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // -----------------------------
  // 加载角色和部门列表
  // -----------------------------
  useEffect(() => {
    Promise.all([
      dataProvider.getList('permissionRoles', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      }),
      dataProvider.getList('permissionDepartment', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
    ])
      .then(([rolesRes, departmentsRes]) => {
        setRoles(rolesRes.data);
        setDepartments(departmentsRes.data);
        setLoading(false);
      })
      .catch(() => {
        notify('Error loading data', { type: 'warning' });
        setLoading(false);
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    if (!record) return;
    const deptId = record.departmentId;
    setSelectedDepartmentId(deptId);

    // 当 record 有 departmentPositionId 时，填充表单字段
    if (record.departmentPositionId && setValue) {
      setValue('departmentPositionId', record.departmentPositionId);
    }
  }, [record, setValue]);

  useEffect(() => {
    if (!selectedDepartmentId) {
      setPositions([]);
      return;
    }

    dataProvider
      .getList('permissionPosition', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { departmentId: selectedDepartmentId },
      })
      .then((res: any) => {
        setPositions(res.data);
      })
      .catch((e: any) => {
        console.error(e);
        notify('Error loading positions', { type: 'warning' });
        setPositions([]);
      });
  }, [selectedDepartmentId, dataProvider, notify]);

  if (loading) return <span>Loading...</span>;
  if (!record) return <span>No user record found</span>;

  // -----------------------------
  // 保存
  // -----------------------------
  const handleSave = async (data: any) => {
    try {
      const payload: any = {
        userName: data.userName,
        email: data.email,
        departmentId: data.departmentId,
        departmentPositionId: data.departmentPositionId,
        roleId: data.roleId,
      };

      // 新密码为空则不修改
      if (data.newPassword && data.newPassword.trim()) {
        // 使用 Web Crypto 做 SHA-256 哈希
        const enc = new TextEncoder();
        const pwBuf = enc.encode(data.newPassword);
        const hashBuf = await crypto.subtle.digest('SHA-256', pwBuf);
        const hashArray = Array.from(new Uint8Array(hashBuf));
        const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        payload.password = passwordHash;
      }

      await update('permissionUsers', { id: id, data: payload });

      notify('User updated successfully', { type: 'success' });
      setTimeout(() => {
        redirect('list', 'permissionUsers');
      }, 500);
    } catch (error) {
      notify('Update failed', { type: 'error' });
    }
  };

  return (
    <SimpleForm sx={{ backgroundColor: '#ffffff', padding: '36px' }} onSubmit={handleSave}>
      <Box display="flex" flexWrap="wrap" gap={2}>

        {/* 邮箱 */}
        <Box flex="1 1 48%">
          <TextInput
            source="email"
            label="Mailbox"
            fullWidth
            validate={[required(), email()]}
          />
        </Box>

        {/* 用户名（不允许修改） */}
        <Box flex="1 1 48%">
          <TextInput
            source="userName"
            label="User Name"
            fullWidth
            validate={[required()]}
            // disabled
          />
        </Box>

        {/* 修改密码（可空） */}
        <Box flex="1 1 48%">
          <TextInput
            source="newPassword"
            label="New Password (optional)"
            type="password"
            fullWidth
            helperText="Leave empty to keep current password"
          />
        </Box>

        {/* 部门下拉列表 */}
        <Box flex="1 1 48%">
          <SelectInput
            source="departmentId"
            label="Department"
            choices={departments}
            optionText="departmentName"
            optionValue="id"
            fullWidth
            validate={[required()]}
            onChange={(event) => {
              const value = event.target.value;
              const deptId = typeof value === 'string' ? Number(value) : value;
              setSelectedDepartmentId(deptId);
              if (setValue) setValue('departmentPositionId', '');
            }}
          />
        </Box>

        {/* 职级下拉列表（根据部门动态加载） */}
        <Box flex="1 1 48%">
          <SelectInput
            source="departmentPositionId"
            label="Position Title"
            choices={positions}
            optionText="positionName"
            optionValue="id"
            fullWidth
            validate={[required()]}
            disabled={!selectedDepartmentId || positions.length === 0}
          />
        </Box>

        {/* 角色下拉列表 */}
        <Box flex="1 1 48%">
          <SelectInput
            source="roleId"
            label="Role"
            choices={roles}
            optionText="roleName"
            optionValue="id"
            fullWidth
            validate={[required()]}
          />
        </Box>

      </Box>

    </SimpleForm>
  );
};

const UserEdit: React.FC = () => (
  <EditBase resource="permissionUsers">
    <UserEditForm />
  </EditBase>
);

export default UserEdit;
